// ABOUTME: Create screen for a new country×business-line catalog config — pick country + business line, then edit (HCP-111556).
import {Content} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import {
  Alert,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from '@mui/material'
import React, {useState} from 'react'
import {useHistory} from 'react-router-dom'

import {useAllowedHConnectConfigCountries} from '../HConnect/hooks/useAllowedHConnectConfigCountries'
import {useCountryConfigList, useCreateCountryConfig} from './hooks/useCatalogConfig'
import {CATALOG_BUSINESS_LINES, emptyBusinessLineConfig, type CatalogBusinessLine} from './types'

export const CatalogConfigurationCreate = () => {
  const history = useHistory()
  const allowedCountries = useAllowedHConnectConfigCountries()
  const {data: existing, isError: isListError} = useCountryConfigList()
  const [country, setCountry] = useState('')
  const [businessLine, setBusinessLine] = useState<CatalogBusinessLine>('CEM')

  const countryCode = country.trim().toUpperCase()
  const create = useCreateCountryConfig(countryCode, businessLine)

  const isAllowedCountry = allowedCountries.some((c) => c.toUpperCase() === countryCode)
  const isDuplicate = (existing ?? []).some(
    (r) =>
      r.countryCode.toUpperCase() === countryCode &&
      r.businessLine.toUpperCase() === businessLine.toUpperCase()
  )
  // Block create until the existing list has loaded, so the duplicate check can't be bypassed (would overwrite via PUT).
  const canCreate =
    existing !== undefined &&
    !isListError &&
    countryCode.length === 2 &&
    isAllowedCountry &&
    !isDuplicate

  const onCreate = () => {
    if (!canCreate) return
    create.mutate(emptyBusinessLineConfig(), {
      onSuccess: () => history.push(`/catalog-configurations/${countryCode}/${businessLine}`),
    })
  }

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader className="pl-0 pr-0 md:pl-0 md:pr-0">Add catalog configuration</PageHeader>
      </div>
      <Grid item md={4} sm={0} xs={0}>
        <Paper elevation={4} style={{padding: '26px 36px', maxWidth: 520}}>
          <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
            <FormControl fullWidth>
              <InputLabel id="catalog-country-label">Country</InputLabel>
              <Select
                labelId="catalog-country-label"
                label="Country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                data-test-id="create-country"
              >
                {allowedCountries.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="catalog-bl-label">Business line</InputLabel>
              <Select
                labelId="catalog-bl-label"
                label="Business line"
                value={businessLine}
                onChange={(e) => setBusinessLine(e.target.value as CatalogBusinessLine)}
                data-test-id="create-business-line"
              >
                {CATALOG_BUSINESS_LINES.map((bl) => (
                  <MenuItem key={bl} value={bl}>
                    {bl}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isDuplicate && (
              <Alert severity="warning">
                A configuration for {countryCode} · {businessLine} already exists. Edit it from the list
                instead.
              </Alert>
            )}

            {create.isError && (
              <Alert severity="error">
                {String((create.error as Error)?.message ?? 'Failed to create configuration')}
              </Alert>
            )}

            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
              <Button variant="outlined" onClick={() => history.push('/catalog-configurations')}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={undefined}
                disabled={!canCreate || create.isLoading}
                onClick={onCreate}
                data-test-id="create-submit"
              >
                {create.isLoading ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </div>
        </Paper>
      </Grid>
    </Content>
  )
}
